import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Monitor, Smartphone, X, Mail } from "lucide-react";
import { API } from "@/lib/auth";

const BRAND_SHELL = ({ preheader, subject, bodyHtml }) => `<!DOCTYPE html><html><head><meta charset="UTF-8"><meta name="viewport" content="width=device-width,initial-scale=1"><title>${subject}</title></head>
<body style="margin:0;padding:0;background:#f5efe8;font-family:Helvetica,Arial,sans-serif;">
  <span style="display:none;visibility:hidden;overflow:hidden;height:0;width:0;font-size:1px;color:#f5efe8;">${preheader || ""}</span>
  <table role="presentation" width="100%" cellpadding="0" cellspacing="0"><tr><td align="center" style="padding:24px 12px;">
    <table role="presentation" width="600" cellpadding="0" cellspacing="0" style="max-width:600px;width:100%;background:#faf9f6;border-radius:16px;overflow:hidden;">
      <tr><td style="padding:36px 40px 8px 40px;">
        <div style="font-family:Georgia,serif;font-size:22px;color:#1a1a1a;letter-spacing:0.04em;">Malek Dridi</div>
        <div style="text-transform:uppercase;letter-spacing:0.28em;font-size:10px;color:#c4a256;margin-top:4px;">Hair &amp; Beauty</div>
      </td></tr>
      <tr><td style="padding:24px 40px 40px 40px;">${bodyHtml}</td></tr>
      <tr><td style="background:#1a1a1a;color:#faf9f6;padding:28px 40px;font-size:12px;line-height:1.6;">
        <div style="font-family:Georgia,serif;font-size:16px;margin-bottom:6px;">Malek Dridi Hair &amp; Beauty</div>
        <div style="color:rgba(250,249,246,0.65);">The Wee Nook, Paisley · Luxury hair, extensions and education.</div>
      </td></tr>
    </table>
  </td></tr></table>
</body></html>`;

/**
 * Preview modal showing the email exactly as subscribers will receive it,
 * on both desktop (600px width) and mobile (375px width). Also has a
 * "Send to my inbox" quick test-send.
 */
export default function EmailPreviewModal({ open, onClose, subject, preheader, htmlBody }) {
  const [view, setView] = useState("desktop");
  const [testEmail, setTestEmail] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onEsc = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onEsc);
    return () => document.removeEventListener("keydown", onEsc);
  }, [open, onClose]);

  const previewHtml = useMemo(
    () => BRAND_SHELL({ subject, preheader, bodyHtml: htmlBody }),
    [subject, preheader, htmlBody]
  );

  const sendTest = async () => {
    const email = testEmail.trim().toLowerCase();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("Enter a valid test email");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/admin/marketing/send-test`, {
        subject,
        preheader,
        html_body: htmlBody,
        to: email,
      });
      toast.success(`Test sent to ${email}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test send failed");
    } finally {
      setSending(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-black/70" data-testid="email-preview-modal">
      <div className="flex items-center justify-between px-6 py-4 bg-[hsl(var(--cream))] border-b border-[hsl(var(--onyx)/0.1)]">
        <div>
          <div className="overline text-[hsl(var(--gold))]">Preview email</div>
          <div className="font-display text-lg mt-0.5 truncate max-w-md">{subject || "(no subject)"}</div>
        </div>

        <div className="flex items-center gap-2">
          <div className="flex bg-white rounded-full border border-[hsl(var(--onyx)/0.1)] p-1">
            <button
              onClick={() => setView("desktop")}
              data-testid="preview-view-desktop"
              className={`px-4 py-1.5 rounded-full text-xs uppercase tracking-widest inline-flex items-center gap-1.5 transition-colors ${
                view === "desktop"
                  ? "bg-[hsl(var(--onyx))] text-[hsl(var(--cream))]"
                  : "text-[hsl(var(--onyx)/0.7)]"
              }`}
            >
              <Monitor size={12} /> Desktop
            </button>
            <button
              onClick={() => setView("mobile")}
              data-testid="preview-view-mobile"
              className={`px-4 py-1.5 rounded-full text-xs uppercase tracking-widest inline-flex items-center gap-1.5 transition-colors ${
                view === "mobile"
                  ? "bg-[hsl(var(--onyx))] text-[hsl(var(--cream))]"
                  : "text-[hsl(var(--onyx)/0.7)]"
              }`}
            >
              <Smartphone size={12} /> Mobile
            </button>
          </div>
          <button
            onClick={onClose}
            data-testid="preview-close"
            aria-label="Close preview"
            className="w-9 h-9 rounded-full bg-white border border-[hsl(var(--onyx)/0.1)] flex items-center justify-center hover:bg-[hsl(var(--blush)/0.4)]"
          >
            <X size={16} />
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-6 flex justify-center bg-[#e8e2da]">
        <div
          className="bg-white shadow-xl rounded-xl overflow-hidden transition-all"
          style={{ width: view === "desktop" ? "620px" : "380px", maxWidth: "100%" }}
        >
          <iframe
            title="email-preview"
            data-testid="preview-iframe"
            srcDoc={previewHtml}
            className="w-full block"
            style={{ height: view === "desktop" ? "780px" : "700px", border: 0 }}
          />
        </div>
      </div>

      <div className="px-6 py-4 bg-[hsl(var(--cream))] border-t border-[hsl(var(--onyx)/0.1)] flex flex-wrap items-center gap-3">
        <Mail size={14} className="text-[hsl(var(--gold))]" />
        <span className="text-xs uppercase tracking-widest text-[hsl(var(--onyx)/0.6)]">Send test to my inbox</span>
        <input
          type="email"
          value={testEmail}
          onChange={(e) => setTestEmail(e.target.value)}
          placeholder="you@email.com"
          data-testid="preview-test-email"
          className="flex-1 min-w-[220px] h-10 rounded-full bg-white border border-[hsl(var(--onyx)/0.15)] px-4 text-sm outline-none focus:border-[hsl(var(--onyx))]"
        />
        <button
          onClick={sendTest}
          disabled={sending || !testEmail.trim() || !subject.trim() || !htmlBody.trim()}
          data-testid="preview-send-test"
          className="btn-pill btn-primary !text-xs disabled:opacity-40"
        >
          {sending ? "Sending…" : "Send test"}
        </button>
      </div>
    </div>
  );
}
